import {
  cartCreateMutation,
  cartQuery,
  getCurrentLanguageCode,
  getSimpleLanguageCode,
  pollShippingRates,
  setLocalStorage,
  shippingLineUpdateMutation,
} from "../../utils/helpers";
import {
  LANGUAGE_EN_US_CODE,
  LANGUAGE_FR_CODE,
  PELICAN_CHECKOUT_COOKIES_CA,
  PELICAN_CHECKOUT_COOKIES_US,
} from "../../constants/AppConstants";
import {
  CREATE_CHECKOUT,
  FETCH_ERROR,
  GET_SHIPPING_RATES,
  GETTING_SHIPPING_RATES_FAILURE,
  GETTING_SHIPPING_RATES_SUCCESS,
  IS_GETTING_SHIPPING_RATES,
  SET_CHECKOUT,
  UPDATE_CHECKOUT,
} from "../../constants/ActionTypes";
import { fetchStart, fetchSuccess } from "./Common";
import {
  checkForErrors,
  getCartCreateInput,
  getLanguageConfig,
  getShopifyHeaders,
  shopifyFetch,
} from "../../utils/shopify";
import { removeCartId, setCartId } from "../../utils/cookies-checkout";

/**
 * If we have Cart ID saved in our store or cookies
 * We call shopify API to get cart details
 * If we don't have cart ID we create new cart
 * @returns
 */
export const getOrCreateCheckout = () => {
  return (dispatch, getState) => {
    getState().checkout.checkoutId ? dispatch(setCheckout()) : dispatch(createCheckout());
  };
};

/**
 * set checkout (now fetches cart)
 * @returns {object|null}
 */
const setCheckout = () => {
  return (dispatch, getState) => {
    const cartId = getState().checkout.checkoutId;
    dispatch(fetchCheckout(cartId));
  };
};

/**
 * Fetch checkout (now fetches cart using GraphQL)
 * @param cartId
 * @returns {(function(*, *): void)|*}
 */
const fetchCheckout = cartId => {
  return (dispatch, getState) => {
    const client = getState().shopify?.client;
    if (!client) {
      dispatch({ type: FETCH_ERROR, payload: "Shopify client not initialized" });
      return;
    }
    client
      .fetch(cartQuery, {
        variables: {
          id: cartId,
        },
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
        }
      })
      .then(({ data, errors }) => {
        if (errors && errors.length > 0) {
          console.error("Cart fetch GraphQL errors:", errors);
          dispatch(updateCheckout(null));
          dispatch({ type: FETCH_ERROR, payload: "Failed to fetch cart" });
          return;
        }

        const cart = data?.cart;
        if (cart) {
          dispatch(fetchSuccess());
          dispatch({
            type: SET_CHECKOUT,
            payload: cart,
          });
        } else {
          dispatch(updateCheckout(null));
          dispatch(createCheckout());
        }
      })
      .catch(err => {
        console.error("Cart fetch error:", err);
        dispatch(updateCheckout(null));
        dispatch({ type: FETCH_ERROR, payload: err.message });
      });
  };
};

/**
 * create checkout (now creates a cart using GraphQL)
 * @returns {object} cart|null
 */
const createCheckout = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const client = state.shopify?.client;

    if (!client) {
      console.log("Shopify client not found");
      return dispatch({
        type: FETCH_ERROR,
        payload: "Shopify client not initialized",
      });
    }

    const { countryCode, languageCode } = getLanguageConfig();
    const customerAccessToken = state.customer?.accessToken;

    dispatch(fetchStart());

    try {
      const input = getCartCreateInput(countryCode, customerAccessToken);
      const headers = getShopifyHeaders(state.shopify.storefrontAccessToken);

      const { data, errors } = await shopifyFetch(
        client,
        cartCreateMutation,
        { input, country: countryCode, language: languageCode },
        headers
      );

      const cartData = checkForErrors(data, errors, "cartCreate");

      if (!cartData) {
        return dispatch({ type: FETCH_ERROR, payload: "Failed to create cart" });
      }

      const cart = cartData.cart;
      setCartId(cart?.id);
      dispatch(fetchSuccess());
      dispatch({ type: CREATE_CHECKOUT, payload: cart });
    } catch (err) {
      dispatch({ type: FETCH_ERROR, payload: err.message });
    }
  };
};

/**
 * Update Cart
 * @param cart
 * @returns {{payload, type: string}}
 */
export const updateCheckout = cart => {
  if (cart?.id) {
    setCartId(cart.id);
  } else if (cart?.completedAt) {
    removeCartId();
  }

  return {
    type: UPDATE_CHECKOUT,
    payload: cart,
  };
};

/**
 * Get shipping rates (needs to be updated for cart-based shipping)
 * We poll shipping rates until they are ready
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const getShippingRates = () => {
  return (dispatch, getState) => {
    const client = getState().shopify.client;
    const cartId = getState().checkout.checkoutId;
    const cart = getState().checkout;
    if (!cart?.lines?.edges?.length && !cart?.lineItems?.length) {
      return Promise.resolve();
    }

    dispatch({ type: IS_GETTING_SHIPPING_RATES });
    return new Promise((resolve, reject) => {
      pollShippingRates(client, cartId)
        .then(shippingRates => {
          if (shippingRates?.ready) {
            dispatch({
              type: GET_SHIPPING_RATES,
              payload: shippingRates.shippingRates,
            });
            dispatch({
              type: GETTING_SHIPPING_RATES_SUCCESS,
            });
            resolve();
          } else {
            const errorMessage = "Shipping rates not ready after maximum attempts";
            dispatch({
              type: GETTING_SHIPPING_RATES_FAILURE,
              payload: errorMessage,
            });
            console.error(errorMessage);
            reject(new Error(errorMessage));
          }
        })
        .catch(error => {
          dispatch({
            type: GETTING_SHIPPING_RATES_FAILURE,
            payload: error,
          });
          dispatch({ type: FETCH_ERROR, payload: error.message });
          console.error("Error fetching shipping rates:", error);
          reject(error);
        });
    });
  };
};

/**
 * Update checkout with shipping rate (needs cart-based shipping update)
 *
 * @param shippingRateHandle
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const updateCheckoutWithShippingRate = shippingRateHandle => {
  return async (dispatch, getState) => {
    dispatch({ type: IS_GETTING_SHIPPING_RATES });
    const client = getState().shopify.client;
    const cartId = getState().checkout.checkoutId;

    try {
      const mutation = shippingLineUpdateMutation(client, cartId, shippingRateHandle);
      const response = await client.fetch(mutation.query, {
        variables: mutation.variables,
      });

      if (response.ok) {
        const { data, errors } = await response.json();

        if (errors && errors.length > 0) {
          dispatch({
            type: GETTING_SHIPPING_RATES_FAILURE,
            payload: errors,
          });
          return;
        }
        const updateResponse = data.cartDeliveryGroupUpdate || data.checkoutShippingLineUpdate;

        if (updateResponse?.userErrors?.length) {
          dispatch({
            type: GETTING_SHIPPING_RATES_FAILURE,
            payload: updateResponse.userErrors,
          });
        } else {
          dispatch(fetchCheckout(cartId));
          dispatch({
            type: GETTING_SHIPPING_RATES_SUCCESS,
          });
        }
      } else {
        throw new Error(`HTTP error: ${response.status}`);
      }
    } catch (error) {
      dispatch({
        type: GETTING_SHIPPING_RATES_FAILURE,
        payload: error.message,
      });
    }
  };
};
